import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import styled from 'styled-components';

import type { IDlsBlockViewProps } from '@/components/common';
import type { IWeekDay } from '@/types/calendar';

import { HOUR_IN_PX, PADDING_WEEKDAY } from '@/config/variables';
import { otherColors } from '@/styles/colors';
import { CrossedBackgroud } from '@/assets/images';
import { BaseShiftModal } from '@/components/agent/my-schedule';
import { DlsModalContent } from '@/components/common/calendar/DlsModalContent';
import { useMouseClickOutside } from '@/hooks';

interface IProps extends IDlsBlockViewProps {
  day: IWeekDay;
}

const DlsBlockMultidayView = ({ week, dlsPosition, day }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useMouseClickOutside(modalRef, () => setIsModalOpen(false));

  if (!week.dst) return <></>;

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement="top"
      overlay={
        <StyledTooltip id={`tooltip-dls`}>
          <TooltipTitle>{t`dls.tooltipTitle`}</TooltipTitle>
          <TooltipContent>{t`dls.tooltipContent`}</TooltipContent>
        </StyledTooltip>
      }
    >
      <Wrapper
        minutesFromMidnight={dlsPosition?.minutesFromMidnight ?? 0}
        blockDuration={(Math.abs(week.dst.tzOffsetDiff) / 2) * HOUR_IN_PX}
        ref={wrapperRef}
        onClick={event => {
          event.stopPropagation();
          setIsModalOpen(prev => !prev);
        }}
      >
        {wrapperRef.current && week.dst && isModalOpen && (
          <BaseShiftModal
            ref={modalRef}
            parentRef={wrapperRef as React.MutableRefObject<HTMLDivElement>}
            content={<DlsModalContent day={day} dst={week.dst} handleClose={() => setIsModalOpen(false)} />}
          />
        )}
        <span>{t`dls.blockTitle`}</span>
      </Wrapper>
    </OverlayTrigger>
  );
};
export { DlsBlockMultidayView };

const Wrapper = styled.div<{
  minutesFromMidnight: number;
  blockDuration: number;
}>`
  display: flex;
  position: absolute;
  left: 0;
  top: 1px;
  z-index: 1;
  background-color: #ffffff;

  width: 100%;
  height: ${props => props.blockDuration - PADDING_WEEKDAY}px;
  padding-left: ${PADDING_WEEKDAY / 2}px;
  padding-right: ${PADDING_WEEKDAY / 2}px;

  transform: translate3d(0, ${props => props.minutesFromMidnight + 1}px, 0);

  font-size: 10px;
  color: ${otherColors.punch_red};
  border-color: ${otherColors.punch_red};
  border-radius: 8px;
  border-width: 2px;
  border-style: solid;

  cursor: pointer;

  &::before {
    position: absolute;
    content: '';
    width: 100%;
    height: 100%;
    mask-image: url(${CrossedBackgroud});
    background-color: ${otherColors.punch_red_20};
  }

  & > span {
    font-weight: bold;
    padding: 3px;
    text-shadow: -2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff, 2px 2px 0 #fff;
    line-height: 16px;
    user-select: none;
  }
`;

const StyledTooltip = styled(Tooltip)`
  & > .tooltip-inner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    background-color: ${otherColors.outer_space};
    font-size: 13px;
    color: #ffffff;
    line-height: 16px;
    text-align: start;
    width: 263px;
    max-width: 263px;
  }
`;

const TooltipTitle = styled.div`
  font-weight: 600;
`;

const TooltipContent = styled.div`
  font-weight: 400;
`;
